/**
 * Offers - "Available students": who is free, soonest first
 * ---------------------------------------------------------
 * The other way round from the marketplace. Instead of posting a job and
 * waiting, a poster can look at who has said they are free and ask one of them
 * directly - which is what you want at 4pm when the babysitter has cancelled.
 *
 * Sorted by whoever is free soonest, because that is the question people
 * arrive with. Nothing on this page books anything: it leads to asking, and
 * the student decides.
 *
 * Posters only, and the server enforces that - this page is never reachable
 * signed out or as a student.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, MapPin, Clock, Calendar } from 'lucide-react';

import { offerApi, choreApi } from '../services/api';
import { dateTime, travel } from '../utils/format';
import Loader from '../components/Loader';
import EmptyState from '../components/EmptyState';
import Badge from '../components/Badge';
import StarRating from '../components/StarRating';
import { useI18n } from '../hooks/useI18n';

export default function Offers() {
  const { t } = useI18n();
  const [offers, setOffers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [category, setCategory] = useState('');
  const [city, setCity] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    choreApi.categories().then((d) => setCategories(d.categories)).catch(() => {});
  }, []);

  useEffect(() => {
    let active = true;
    setLoading(true);
    offerApi
      .list({ category: category || undefined, city: city || undefined })
      .then((data) => {
        if (active) setOffers(data.offers || []);
      })
      .catch((err) => {
        if (active) setError(err.message);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [category, city]);

  return (
    <div className="container section">
      <div className="page-header">
        <h1>{t('offers.title')}</h1>
        <p>
          {t('offers.subtitle')}
        </p>
      </div>

      {/* ---------------- FILTERS ---------------- */}
      <div className="card" style={{ marginBottom: 'var(--space-lg)' }}>
        <div className="form-row">
          <div className="field">
            <label htmlFor="offer-category">{t('offers.whatDoYouNeed')}</label>
            <select id="offer-category" value={category} onChange={(e) => setCategory(e.target.value)}>
              <option value="">{t('offers.anything')}</option>
              {categories.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
          <div className="field">
            <label htmlFor="offer-city">{t('offers.town')}</label>
            <input
              id="offer-city"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              placeholder="Varese"
            />
          </div>
        </div>
      </div>

      {error && <div className="alert alert-error" style={{ marginBottom: 'var(--space-md)' }}>{error}</div>}

      {loading ? (
        <Loader label={t('offers.loading')} />
      ) : offers.length === 0 ? (
        <EmptyState
          icon={Search}
          title={t('offers.emptyTitle')}
          message={t('offers.emptyBody')}
          action={<Link to="/chores/new" className="btn btn-primary btn-inline">{t('dashboard.postChore')}</Link>}
        />
      ) : (
        <>
          <p className="text-sm text-muted" style={{ marginBottom: 'var(--space-sm)' }}>
            {t('offers.countFree', { count: offers.length })}
          </p>

          {/* ---------------- ONE STUDENT, SHAPED LIKE A CHORE ----------------
              Rebuilt 25 August 2026, at the owner's request: these cards read
              the way a chore card does - name where the title goes, the badges
              under it, what they can do, then the button.

              They used to LEAD with the person: a large photograph, the stars
              and the number of jobs done, all before anything about the work.
              That is a profile, and it made a page of them scan as a directory
              of children rather than a list of what is on offer - which is the
              wrong thing to put in front of an adult browsing. The rating and
              the photograph are still reachable, one tap away, where somebody
              is deciding about a particular person rather than skimming.

              Same markup as components/ChoreCard.jsx on purpose. If that card
              changes shape, this one should follow it. */}
          <div className="stack" style={{ gap: 'var(--space-sm)' }}>
            {offers.map((offer) => (
              <article className="card card-hover" key={offer._id}>
                <div className="card-header">
                  <Link to={`/offers/${offer._id}`} className="card-title">
                    {offer.student.firstName} {offer.student.lastName?.[0]}.
                  </Link>
                  <Badge tone="amber">{offer.student.schoolYear}</Badge>
                </div>

                <div className="row" style={{ gap: 'var(--space-xs)', marginBottom: 'var(--space-xs)', flexWrap: 'wrap' }}>
                  {/* The stored value is the English enum and only the LABEL is
                      translated - the same rule as ChoreCard, and the reason is
                      at the top of i18n/en.js. These used to print the raw
                      value, so an Italian poster read "Pet Care" next to a
                      chore that said "Cura animali" for the same category. */}
                  {offer.categories.map((c) => (
                    <Badge key={c} tone="soft-blue">{t(`categories.${c}`)}</Badge>
                  ))}

                  {offer.distanceKm !== null && offer.distanceKm !== undefined && (
                    <Badge tone="soft-amber" icon={MapPin}>
                      {travel(offer)}
                    </Badge>
                  )}
                </div>

                <p className="card-body-text">{offer.description}</p>

                <div className="card-meta">
                  {/* Two different things, so say which is which. The town is
                      where they are willing to work; the distance badge above
                      is from you to them. "Ispra (2.9 km away)" read as though
                      Ispra were 2.9km away. */}
                  <span>
                    <MapPin size={14} aria-hidden="true" />
                    {t('offers.worksIn', { city: offer.area?.city })}
                  </span>
                  <span>
                    <Calendar size={14} aria-hidden="true" />
                    {t('offers.nextFree')} {dateTime(offer.nextFree)}
                  </span>
                  {offer.slotCount > 1 && (
                    <span>
                      <Clock size={14} aria-hidden="true" />
                      {t('offers.timesAvailable', { count: offer.slotCount })}
                    </span>
                  )}
                </div>

                <div className="card-footer">
                  <span className="text-sm text-muted">
                    <StarRating value={offer.student.rating?.average || 0} size={13} />{' '}
                    {t('offers.jobsDone', { count: offer.student.stats?.choresCompleted || 0 })}
                  </span>

                  <Link to={`/offers/${offer._id}`} className="btn btn-primary btn-sm btn-inline">
                    {t('offers.seeTheirTimes')}
                  </Link>
                </div>
              </article>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
